import React from 'react';
import { observer } from 'mobx-react-lite';

import { MembersList } from '../../../routes/project/components/shootingday/members-list';
import { CollapsibleItem } from '../../../components/collapsible-item/collapsible-item';
import { TableContainer } from '../../../components/table/styled-shared-table';
import { EmptyTablePlaceholder } from '../../../components/table/empty-table-placeholder';
import { ConditionalWrapper } from '../../../components/conditional-wrapper/conditional-wrapper';
import { useResponsive } from '../../../hooks/useResponsive';

import type { CrewMember } from '../../../app/entities/crew-member';
import type { Step } from '../../../app/entities/step';
import type { TeamContact } from '../../teams/models/team';

type CrewCallTimesProps = {
  members: CrewMember[];
  step: Step;
};

/**
 * Replaces the old per-team `TeamItem` for the flat crew list. Name, role, department, team,
 * email and phone are owned by the Crew screen now (`CrewList`, read-only) — the one thing this
 * screen still needs to write is per-day call time and hide/show, so that stays here.
 *
 * Call-time and hide writes are keyed by `crewMemberId` (the position id), never `contactId`.
 * Migrations 0008 and 8b exist precisely so a call time belongs to a POSITION, not a person — two
 * positions can share one contact and need two different call times. `Step.saveContactMeta`
 * (server) dedupes on whichever key the payload carries, so no API change is needed here; only
 * the key this component sends had to change from `Team`'s `contactId`-only shape.
 *
 * `step.toggleHideContact` is deliberately NOT reused here — it hardcodes `contactId`, which is
 * right for the still-untouched Executive/Cast sections but would silently no-op for crew.
 */
export const CrewCallTimes: React.FC<CrewCallTimesProps> = observer(({ members, step }) => {
  const { isDesktop } = useResponsive();

  // MembersList/MembersTable are TeamContact-shaped and keyed on `_id` for both display and the
  // update callbacks below — the crew position id (`member._id`) fills that role directly.
  const contacts = members.map((member) => ({
    _id: member._id,
    firstName: member.name,
    lastName: '',
    role: member.role,
    email: member.email,
    phone: member.phone,
  })) as TeamContact[];

  // `MembersList` indexes its `meta` prop internally by `meta.contactId` (right for the
  // Executive/Cast tables it also serves) — it has no notion of `crewMemberId`. Rather than
  // change that shared lookup, hand it a crew-only view of the metadata with `contactId`
  // shadowed to the position id, so its existing by-`_id` matching lines up with `contacts`
  // above. This is a read-side reshape only; writes below already send the real key.
  const crewMeta = step.contactsMetaData
    ?.filter((meta) => !!meta.crewMemberId)
    .map((meta) => ({ ...meta, contactId: meta.crewMemberId }));

  const handleMetaUpdate = async (
    crewMemberId: string,
    values: Omit<
      Exclude<Step['contactsMetaData'], undefined>[number],
      'contactId' | 'crewMemberId'
    >,
  ) => {
    // @ts-expect-error — `update()` accepts one contactsMetaData entry to merge, not the full array;
    // same shortcut the deleted `team-item.tsx` used for its contactId entries.
    await step.update({ contactsMetaData: { crewMemberId, ...values } });
  };

  const handleToggleHide = async (crewMemberId: string) => {
    const current = step.contactsMetaData?.find((meta) => meta.crewMemberId === crewMemberId);

    // @ts-expect-error — see handleMetaUpdate.
    await step.update({ contactsMetaData: { crewMemberId, isHidden: !current?.isHidden } });
  };

  return (
    <ConditionalWrapper
      condition={isDesktop}
      wrapper={(children) => (
        <CollapsibleItem title="Crew" defaultOpen>
          <TableContainer>{children}</TableContainer>
        </CollapsibleItem>
      )}
    >
      <MembersList
        title="Crew"
        meta={crewMeta}
        onMetaUpdate={handleMetaUpdate}
        onToggleHide={handleToggleHide}
        contacts={contacts}
        defaultCallTime={{
          hour: step.info?.generalCallTime?.hour || 0,
          minute: step.info?.generalCallTime?.minute || 0,
        }}
        placeholder={
          !contacts.length && <EmptyTablePlaceholder description="No crew matches these filters." />
        }
      />
    </ConditionalWrapper>
  );
});
